import { useCallback, useEffect, useState } from "react";

import { fetchProfile } from "@/entities/Auth";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { useAppDispatch, useAppSelector } from "@/shared/lib/hooks/redux";
import { useTheme } from "@/shared/lib/hooks/useTheme";
import { Dialog } from "@/shared/ui/Dialog";

import { getProductsList } from "../../model/selectors/subscriptionSelectors";
import { fetchProducts } from "../../model/services/fetchProducts";
import { upgradeSubscription } from "../../model/services/upgradeSubscription";
import { ProductSelection } from "../ProductSelection/ProductSelection";

import cls from "./UpgradeModal.module.scss";

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * User chose not to upgrade.
   */
  onContinueAnyway: () => void;
  /**
   * Called only after an upgrade succeeds and the profile has been refreshed.
   */
  onSuccessfulUpgrade: () => void;
}

export const UpgradeModal = ({ isOpen, onClose, onContinueAnyway, onSuccessfulUpgrade }: UpgradeModalProps) => {
  const dispatch = useAppDispatch();
  const { showAlert } = useAlert();
  const { theme } = useTheme();

  const productsList = useAppSelector(getProductsList);

  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (isOpen && productsList.length === 0) {
      dispatch(fetchProducts(undefined));
    }
  }, [isOpen, productsList.length, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedProduct(null);
    }
  }, [isOpen]);

  const handleUpgrade = useCallback(async () => {
    if (!selectedProduct) {
      return;
    }

    setIsBusy(true);
    try {
      const result = await dispatch(upgradeSubscription({ toPriceId: selectedProduct }));

      if (upgradeSubscription.rejected.match(result)) {
        const error = result.payload as { details?: string } | undefined;
        showAlert({
          theme: AlertTheme.ERROR,
          message: error?.details || "Failed to upgrade subscription",
        } as AlertOptions);
        return;
      }

      showAlert({
        theme: AlertTheme.SUCCESS,
        message: "Subscription successfully upgraded!",
      } as AlertOptions);

      await dispatch(fetchProfile());
      onSuccessfulUpgrade();
    } finally {
      setIsBusy(false);
    }
  }, [dispatch, selectedProduct, showAlert, onSuccessfulUpgrade]);

  return (
    <Dialog
      isOpen={isOpen}
      isBusy={isBusy}
      onClose={onClose}
      title={
        <div className={cls.ModalTitle}>
          Unlock unlimited readings with this advisor!
          <br />
          Upgrade your plan to continue without extra fees ✨
        </div>
      }
      submit={{
        text: "Upgrade Now",
        onClick: handleUpgrade,
        disabled: !selectedProduct,
      }}
      cancel={{
        text: "Continue Anyway",
        onClick: onContinueAnyway,
      }}
    >
      <ProductSelection
        variant={theme === "darkTheme" ? "dark" : "light"}
        stripePriceId={selectedProduct}
        onChange={setSelectedProduct}
        showOnlyHigherPlans={true}
      />
    </Dialog>
  );
};
